/**
 * Toast Notification System — Enterprise Design System
 *
 * Provides a global toast notification system with:
 *  - Success, Error, Warning, Info variants
 *  - Auto-dismiss with configurable duration
 *  - Stack support (multiple toasts)
 *  - Smooth enter/exit animations
 *  - Accessible announcements via aria-live
 *
 * Usage:
 *   import { ToastProvider, useToast } from '../design-system';
 *
 *   // Wrap app in ToastProvider (once, near root)
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 *   // In any component:
 *   const toast = useToast();
 *   toast.success('Record saved successfully');
 *   toast.error('Failed to save');
 *   toast.warning('Session expiring soon');
 *   toast.info('New update available');
 */

import React, { createContext, useContext, useCallback, useState, useRef } from 'react';
import { Snackbar, Alert, Slide, Stack } from '@mui/material';

// ── Context ───────────────────────────────────────────────────────────────────

const ToastContext = createContext(null);

/** Hook to access toast methods */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Return no-ops outside provider for safety
    return {
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
      dismiss: () => {},
    };
  }
  return ctx;
}

// ── Slide Transition ──────────────────────────────────────────────────────────

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

// ── Provider ──────────────────────────────────────────────────────────────────

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 4000;

let toastIdCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const addToast = useCallback((severity, message, options = {}) => {
    const id = ++toastIdCounter;
    const duration = options.duration ?? DEFAULT_DURATION;
    const toast = { id, severity, message, duration };

    setToasts((prev) => {
      const updated = [...prev, toast];
      // Trim oldest if exceeding max
      if (updated.length > MAX_TOASTS) {
        const removed = updated.shift();
        if (removed && timersRef.current[removed.id]) {
          clearTimeout(timersRef.current[removed.id]);
          delete timersRef.current[removed.id];
        }
      }
      return updated;
    });

    // Auto-dismiss
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => {
        dismiss(id);
      }, duration);
    }

    return id;
  }, [dismiss]);

  const api = {
    success: (message, options) => addToast('success', message, options),
    error: (message, options) => addToast('error', message, { duration: 6000, ...options }),
    warning: (message, options) => addToast('warning', message, options),
    info: (message, options) => addToast('info', message, options),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Toast stack */}
      <Stack
        spacing={1}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 2000,
          maxWidth: 420,
          width: '100%',
          pointerEvents: 'none',
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((toast) => (
          <Snackbar
            key={toast.id}
            open
            TransitionComponent={SlideTransition}
            sx={{
              position: 'relative',
              transform: 'none',
              bottom: 'unset',
              left: 'unset',
              right: 'unset',
              pointerEvents: 'auto',
            }}
          >
            <Alert
              severity={toast.severity}
              variant="filled"
              onClose={() => dismiss(toast.id)}
              sx={{
                width: '100%',
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                fontWeight: 500,
                fontSize: '0.8125rem',
                alignItems: 'center',
              }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
